// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import dynamic from 'next/dynamic';

import styled from '@emotion/styled';

import Tabs from 'presentations/views/components/atoms/Tabs';
import {
  gray04,
  white,
  primary,
  whiteFix,
} from 'presentations/views/styles/color';
import {
  mediaMax,
  spacing,
  zIndex,
  borderRadius,
  fontSize,
} from 'presentations/views/styles';
import UserListFilterNavigation, {
  UserListFilter,
} from 'presentations/views/components/organisms/UserListFilterNavigation';
import { useDashboardHomeNavigation } from 'presentations/views/components/organisms/DashboardHomeNavigation/hooks';
import { ViewerItem } from 'presentations/api/schema/user';
import DashboardHeaderTitle from 'presentations/views/components/organisms/DashboardHeaderTitle';
import { DisplayType } from 'domains/models/project/displayType';
import { useProjectHeader } from './hooks/projectHeader';
import { canEditProject as canEditProjectFunc } from 'presentations/views/helpers/user_permission';
import { ProjectItem } from 'presentations/api/schema/project';

const ProjectForm = dynamic(() =>
  import('presentations/views/components/organisms/ProjectForm'),
);
const MemberListDialog = dynamic(() => import('./dialog/MemberListDialog'));
const MemberInviteDialog = dynamic(() => import('./dialog/MemberInviteDialog'));
const ConfirmationDialog = dynamic(() =>
  import('presentations/views/components/molecules/ConfirmationDialog'),
);

interface Props {
  activeIndex: number | null;
  onChangeFilter?: (filters: UserListFilter) => void;
  projectId: string;
  viewer: ViewerItem;
  project?: ProjectItem;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const { state, actions } = useDashboardHomeNavigation({
    viewer: props.viewer,
    userId: null,
    projectId: props.projectId,
  });
  const projectHeader = useProjectHeader({
    viewer: props.viewer,
    project: props.project,
  });
  const project = projectHeader.state.project;

  const tabs: any = [
    {
      label: t('Home'),
    },
    {
      label: (
        <Todo>
          {t('Schedules')}
          {state.schedulesCount > 0 && (
            <TodoCount>{state.schedulesCount}</TodoCount>
          )}
        </Todo>
      ),
    },
    {
      label: t('Form'),
    },
  ];
  const canEditProject = canEditProjectFunc(project, props.viewer);

  return (
    <>
      <UserListFilterNavigation
        teamUsers={state.teamUsers}
        userId={null}
        projectId={props.projectId}
        onChangeFilter={props.onChangeFilter}
        viewer={props.viewer}
      />

      <HeaderContainer>
        <DashboardHeaderTitle
          title={project?.name}
          titleIconSuffix={
            project?.displayType === DisplayType.Private ? 'lock' : null
          }
          emojiIconName={project?.icon}
          users={project?.users}
          onClickUserList={projectHeader.actions.onClickMemberList}
          onClickSettings={
            canEditProject ? projectHeader.actions.onClickSettings : undefined
          }
          onClickDeleteProject={
            canEditProject
              ? projectHeader.actions.confirmDeleteProject
              : undefined
          }
          onClickInvite={projectHeader.actions.onClickInvite}
          isLoading={!projectHeader.state.isProjectLoaded}
          menuOptions={projectHeader.state.menuOptions}
        />
      </HeaderContainer>

      <NavigationContainer>
        <Tabs
          indicatorColor="primary"
          value={props.activeIndex}
          onChange={actions.onChangeTab}
          tabs={tabs}
        />
      </NavigationContainer>

      <ProjectForm
        isCreateMode={false}
        {...projectHeader.state.projectForm}
        {...projectHeader.actions.projectForm}
      />
      <MemberListDialog
        isDeletable={projectHeader.state.isDeletable}
        {...projectHeader.state.memberListDialog}
        {...projectHeader.actions.memberListDialog}
      />
      <MemberInviteDialog
        {...projectHeader.state.memberInviteDialog}
        {...projectHeader.actions.memberInviteDialog}
      />
      <ConfirmationDialog
        isLoading={projectHeader.state.isConfirmLoading}
        isOpen={projectHeader.state.deleteProject !== null}
        onSubmit={projectHeader.actions.onDeleteProject}
        onCancel={projectHeader.actions.onCloseDeleteProject}
        title={t('Delete Project?')}
        submitLabel={t('Delete')}
      />
    </>
  );
}

const NavigationContainer = styled.div`
  padding: 0 ${spacing(24)};
  background-color: ${white};
  position: sticky;
  top: calc(81px + 62px);
  left: 0;
  right: 0;
  ${mediaMax.tablet} {
    top: calc(81px + 37px);
  }
  z-index: ${zIndex(1100)};
  border-bottom: 1px solid ${gray04};
`;

const HeaderContainer = styled.div`
  position: sticky;
  top: 81px;
  left: 0;
  right: 0;
  z-index: 1;
`;
const Todo = styled.div`
  display: inline-flex;
  align-items: center;
`;
const TodoCount = styled.span`
  margin-left: ${spacing(8)};
  padding: ${spacing(2)} ${spacing(8)};
  background-color: ${primary};
  border-radius: ${borderRadius(16)};
  color: ${whiteFix};
  font-size: ${fontSize(11)};
`;
